import {
  Route,
  Fence,
  Trees,
  Scissors,
  Container,
  Droplets,
  ShowerHead,
  PaintBucket,
  House,
  Leaf,
  TowerControl,
  Axe,
  RadioTower,
  Flame,
  Grid3x3,
  Layers,
  BrickWall,
  AirVent,
  Blinds,
  type LucideIcon,
} from 'lucide-react-native';
import type { ComponentType } from 'react';
import { GoatIcon } from './icons/GoatIcon';
import { hexWithAlpha, mixHex, relativeLuminance } from '../../theme/colorMix';
import type { DefenseFeatureKind, DefenseStrategy, PlanDrawKind } from '../../types';

/**
 * Strategy palette for the defense plan drawing tool (Glenn's Sept 9 2026
 * call). Shaped like fhszColors.ts / fireHistoryColors.ts: one file holds
 * the display definitions for a map overlay, shared between the drawing
 * layer and the panel/legend so they can't drift apart.
 *
 * Glenn's icon-sheet colours mostly collide with the marker grammar (green
 * = progress, red = risk, purple = news, blue = delta, orange = selected,
 * gold = FHSZ moderate). Only his teal (water-tank) survives unchanged.
 * These eleven hexes are the one thing to put in front of Glenn for a
 * colour review; this file is the single source of truth so each is a
 * one-line change.
 */
export interface DefenseStrategyDef {
  label: string;
  description: string;
  stroke: string;
  dashArray?: string;
  // A lucide icon, or a local one (GoatIcon) where lucide has no glyph.
  icon: LucideIcon | ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  // The kind the panel arms when this strategy is picked with no kind
  // already chosen (DefensePlanPanel's handleStrategyPress). Line for every
  // ground strategy that can be drawn as one (Oct 1 2026, Glenn: lines are
  // quicker to lay out than areas; Area is one click away in "Draw as"),
  // point only where a line makes no sense (a tank, a tower). 'home' only
  // ever appears here for an appliesTo:'home' strategy — see PlanDrawKind's
  // doc comment for what picking it actually commits.
  defaultKind: PlanDrawKind;
  // What the strategy protects: 'home' for defenses applied to the
  // structure itself (hardening, active sprinklers, paint, wetting) — the
  // panel defaults these to draw kind 'home' (pick a house on the map) —
  // 'ground' for everything drawn on the land around it, which still
  // defaults to line/point/area above.
  appliesTo: 'home' | 'ground';
  // True for a strategy no longer offered in the panel's palette or the
  // legend — kept in DEFENSE_STRATEGIES (and the zod enum) only so an
  // existing plan drawn with it still parses and renders. Never appears in
  // DEFENSE_STRATEGY_ORDER.
  hidden?: boolean;
  // Optional external reference for the strategy, shown as a "Learn more"
  // link next to the property page's neighborhood-plan suggestion (Sept 10
  // 2026, workstream D). Unset for every strategy today — nothing to link
  // to yet.
  learnMoreUrl?: string;
}

export const DEFENSE_STRATEGIES: Record<DefenseStrategy, DefenseStrategyDef> = {
  'fire-break': {
    label: 'Fire break',
    description: 'A cleared strip that stops a ground fire spreading.',
    stroke: '#8A5A2B',
    icon: Route,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'metal-fence': {
    // Moved to the Home group Oct 14 2026 (Glenn's method list). Stored plans
    // may still hold metal-fence LINE features; those render and copy fine
    // (copyTargetStrategies decides by the feature, not this field).
    label: 'Metal fence',
    description: 'A non-combustible fence line that will not carry fire to a structure.',
    stroke: '#334155',
    dashArray: '6 4',
    icon: Fence,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'tree-thinning': {
    label: 'Thin trees',
    description: 'Removing selected trees so a canopy fire cannot jump between them.',
    stroke: '#7CB518',
    dashArray: '2 6',
    icon: Trees,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'brush-clearing': {
    label: 'Clear brush',
    description: 'Cutting back living brush and shrubs that would carry fire toward a structure.',
    stroke: '#C2298A',
    icon: Scissors,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'water-tank': {
    label: 'Water tank',
    description: 'A stored water supply for fire crews or a sprinkler system.',
    stroke: '#0E8F8B',
    icon: Container,
    defaultKind: 'point',
    appliesTo: 'ground',
  },
  // Glenn, Oct 8 2026: "that's what a lot of companies are selling".
  'fire-sensor': {
    label: 'Fire sensor',
    description: 'An electronic sensor that detects a fire early and sends an alert.',
    stroke: '#D9480F',
    icon: RadioTower,
    defaultKind: 'point',
    appliesTo: 'ground',
  },
  // Superseded by 'sprinklers' (Sept 9 2026 rename below) — an automated
  // exterior sprinkler platform replaced the manual hose-and-line concept
  // this id was for. Kept, hidden and relabeled to match rather than
  // removed: an existing plan drawn with it still has to parse
  // (defenseStrategySchema keeps the enum value), and a stray render of it
  // (an old plan's feature list, say) should read as the same idea under
  // its current name rather than a name nothing else uses anymore.
  'home-wetting': {
    label: 'Active wildfire defense',
    description: 'A hose or fixed line that wets a structure ahead of a fire front.',
    stroke: '#3D4EDB',
    icon: Droplets,
    defaultKind: 'line',
    appliesTo: 'home',
    hidden: true,
  },
  // Renamed Sept 9 2026 after Glenn's Frontline email: the strategy stands
  // for an automated exterior sprinkler platform (fire detected by satellite
  // and sensors, sprinklers keep the home too wet to burn), not a hose on a
  // pole. The stored id stays 'sprinklers' so existing plans do not move.
  sprinklers: {
    label: 'Active wildfire defense',
    description: 'Automated exterior sprinklers that switch on when a fire is detected nearby.',
    stroke: '#5EC8F2',
    icon: ShowerHead,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'fireproof-paint': {
    label: 'Surface treatment',
    description: "A fire-retardant coating or treatment applied to a structure's surfaces.",
    stroke: '#111827',
    icon: PaintBucket,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  // Four strategies added Sept 16 2026 to round out the palette. Colours
  // picked to stay clear of both the seven strokes above and the map's
  // reserved meanings (readiness green/red, news purple, delta blue,
  // selected orange, FHSZ gold) — see the file header note, still awaiting
  // Glenn's review like the rest.
  'house-hardening': {
    label: 'General hardening',
    description:
      'Ember-resistant vents, a cleared 5-foot zone around the foundation, and non-combustible roofing or siding.',
    stroke: '#968369',
    icon: House,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  // Kept separate from brush-clearing on purpose (Sept 16 2026). On the call
  // Glenn said brush clearing is "really clean up fuel", but his email the
  // same day asked to ADD "remove fuel". The work differs: brush clearing
  // cuts living plants, this picks up dead material already on the ground.
  'remove-fuel': {
    label: 'Remove surface fuel',
    description: 'Picking up dead fuel on the ground: needles, leaves, fallen branches and debris.',
    stroke: '#515926',
    icon: Leaf,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'sprinkler-towers': {
    label: 'Sprinkler towers',
    description: 'Tall standalone sprinklers that wet a wide area around a home.',
    stroke: '#2D206F',
    icon: TowerControl,
    defaultKind: 'point',
    appliesTo: 'ground',
  },
  'limb-trees': {
    label: 'Limb trees',
    description: 'Removing lower branches so a ground fire cannot climb into the canopy.',
    stroke: '#4E6B3A',
    icon: Axe,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  // Seven strategies added Oct 14 2026 from Glenn's method list. Strokes
  // chosen clear of the thirteen above and of the reserved map hues
  // (readiness green/red, news purple, delta blue, selected orange, FHSZ
  // gold, teal community); they await Glenn's colour review like the rest.
  grazing: {
    label: 'Grazing',
    description: 'Goats or other livestock grazing down the brush and grass that would carry fire.',
    stroke: '#B8736B',
    icon: GoatIcon,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'prescribed-fire': {
    label: 'Prescribed fire',
    description: 'A planned, controlled burn that clears fuel before a wildfire can use it.',
    stroke: '#7A1F1F',
    icon: Flame,
    defaultKind: 'line',
    appliesTo: 'ground',
  },
  'defense-mesh': {
    label: 'Defense mesh',
    description: 'Fine metal mesh under eaves and over openings that keeps embers out.',
    stroke: '#9AA5B1',
    icon: Grid3x3,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'upgrade-roofing': {
    label: 'Upgrade roofing',
    description: 'Replacing the roof covering with a Class A fire-rated material.',
    stroke: '#6F4E37',
    icon: Layers,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'upgrade-siding': {
    label: 'Upgrade siding',
    description:
      'Replacing combustible siding with a non-combustible or ignition-resistant material.',
    stroke: '#D08C60',
    icon: BrickWall,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'upgrade-vents': {
    label: 'Upgrade vents',
    description: 'Ember-resistant vents that keep embers out of the attic and crawl space.',
    stroke: '#5B7F95',
    icon: AirVent,
    defaultKind: 'home',
    appliesTo: 'home',
  },
  'upgrade-windows': {
    label: 'Upgrade windows',
    description: 'Tempered or multi-pane windows that resist radiant heat and breaking.',
    stroke: '#A89CC8',
    icon: Blinds,
    defaultKind: 'home',
    appliesTo: 'home',
  },
};

/** The palette's two labeled groups, Landscape first (Glenn, Oct 14 2026:
 *  home defenses attach to a house and read apart from the lines, areas and
 *  points drawn on the land). Each group's key must match its members'
 *  `appliesTo`, and every non-hidden strategy must appear in exactly one
 *  group; defenseStrategies.test.ts enforces both. A hidden strategy
 *  (DefenseStrategyDef.hidden) still parses and renders wherever an old plan
 *  drew it, but never appears here, so a new plan can't pick it. */
export const STRATEGY_GROUPS: {
  key: 'ground' | 'home';
  label: string;
  strategies: DefenseStrategy[];
}[] = [
  {
    key: 'ground',
    label: 'Landscape',
    strategies: [
      'brush-clearing',
      'fire-break',
      'fire-sensor',
      'grazing',
      'limb-trees',
      'prescribed-fire',
      'remove-fuel',
      'sprinkler-towers',
      'tree-thinning',
      'water-tank',
    ],
  },
  {
    key: 'home',
    label: 'Home',
    strategies: [
      'house-hardening',
      'sprinklers',
      'defense-mesh',
      'metal-fence',
      'fireproof-paint',
      'upgrade-roofing',
      'upgrade-siding',
      'upgrade-vents',
      'upgrade-windows',
    ],
  },
];

// Display order for the panel's palette and the legend: the groups, flattened,
// so the two cannot diverge. Digits 1-9 pick the first nine, all Landscape:
// 1 Clear brush, 2 Fire break, 3 Fire sensor, 4 Grazing, 5 Limb trees,
// 6 Prescribed fire, 7 Remove surface fuel, 8 Sprinkler towers, 9 Thin trees.
// The tenth Landscape entry (Water tank) and the Home group have no digit.
export const DEFENSE_STRATEGY_ORDER: DefenseStrategy[] = STRATEGY_GROUPS.flatMap(
  (g) => g.strategies
);

const GROUND_KINDS: PlanDrawKind[] = ['line', 'point', 'area'];
const HOME_KINDS: PlanDrawKind[] = ['home'];

/** The shapes a strategy can be drawn as: "Draw as" offers these for a
 *  ground strategy, a home strategy has exactly one. */
export function kindOptionsForStrategy(strategy: DefenseStrategy): PlanDrawKind[] {
  return DEFENSE_STRATEGIES[strategy].appliesTo === 'home' ? HOME_KINDS : GROUND_KINDS;
}

/** The strategies a feature can be copied as ("Copy as another defense"), in
 *  palette order. The feature decides, not its current strategy's appliesTo
 *  (metal-fence moved from ground to home on Oct 14 2026, so stored plans hold
 *  metal-fence lines): a feature pinned to a structure offers home
 *  strategies; any other feature offers the ground strategies that take its
 *  kind. Never its own strategy, never a hidden one. */
export function copyTargetStrategies(feature: {
  strategy: DefenseStrategy;
  kind: DefenseFeatureKind;
  structureId?: string;
}): DefenseStrategy[] {
  const isHome = !!feature.structureId;
  return DEFENSE_STRATEGY_ORDER.filter((strategy) => {
    const def = DEFENSE_STRATEGIES[strategy];
    if (strategy === feature.strategy || def.hidden) return false;
    if (isHome) return def.appliesTo === 'home';
    return def.appliesTo === 'ground' && kindOptionsForStrategy(strategy).includes(feature.kind);
  });
}

// A strategy's badge treatment for the panel's tiles and feature rows: a
// soft tint of the strategy color behind an icon stroked in that same
// color, so the color reads as "this is its color on the map" (the same
// hue PlanStrokeSwatch/PlanAreaSwatch draw in the legend) instead of the
// arbitrary 12x3 line the tiles used to carry.
//
// Two of the eight strokes (metal-fence, fireproof-paint) are dark enough
// to nearly vanish on the dark panel background; rather than hand-picking a
// dark-mode hex for just those two (and re-picking it if Glenn's colour
// review changes them), this lightens ANY stroke that's too dark to read in
// dark mode by blending it toward white, so a future dark strategy color
// gets the same treatment automatically.
const DARK_MODE_LUMINANCE_FLOOR = 0.35;
const DARK_MODE_LIGHTEN_AMOUNT = 0.55;

export interface StrategyBadgeColors {
  icon: string;
  tint: string;
}

export function strategyBadgeColors(
  strategy: DefenseStrategy,
  isDark: boolean
): StrategyBadgeColors {
  const { stroke } = DEFENSE_STRATEGIES[strategy];
  const icon =
    isDark && relativeLuminance(stroke) < DARK_MODE_LUMINANCE_FLOOR
      ? mixHex(stroke, '#ffffff', DARK_MODE_LIGHTEN_AMOUNT)
      : stroke;
  return {
    icon,
    tint: hexWithAlpha(icon, isDark ? 0.22 : 0.12),
  };
}

// Casing drawn under every plan line/point so a dark stroke survives the
// satellite basemap, the same treatment fireHistoryColors.ts uses. Areas
// don't get a casing — the fill already reads against the satellite
// basemap, and a white ring around a filled shape looks like a hole.
export const PLAN_CASING = '#FFFFFF';
export const PLAN_CASING_OPACITY = 0.5;
export const PLAN_LINE_WEIGHT = 3;
export const PLAN_CASING_EXTRA_WEIGHT = 2;
export const PLAN_AREA_FILL_OPACITY = 0.18;
